import { KazooError, KeyOfType } from "@kazoohr/types";

type HierarchyKey<TEntity, THierarch> = KeyOfType<
  TEntity,
  THierarch | null | undefined
>;

/**
 * Attempts to sort entities so that entities without hierarchs (think manager or parent) appear at the start and people deep in the hierarchy appear at the end
 *
 * maxIterations limits the damage if somebody gives this a circular list
 */
export function sortByHierarchy<
  TEntity extends Record<string, any>,
  THierarch
>({
  entities,
  options = {
    maxIterations: 100,
  },
  entityMatches,
  hierarchyKey,
  hierarchMatches,
}: {
  entities: TEntity[];
  // the key on entity that contains a hierarch. e.g. entity.manager
  hierarchyKey: HierarchyKey<TEntity, THierarch>;
  // function that determines if two entities are the same: e.g. entity1.id===entity2.id
  entityMatches: (entity1: TEntity, entity2: TEntity) => boolean;
  // function that determines if an entity and a hierarch are the same e.g. entity.email===hierarch.key
  hierarchMatches: (entity: TEntity, hierarch: THierarch) => boolean;
  options?: { throwOnTooManyIterations?: true; maxIterations: number };
}) {
  const notInResults = (entity: TEntity) =>
    !ordered.some((orderedEntity) => entityMatches(orderedEntity, entity));

  const hasHierarch = (entity: TEntity) => !!entity[hierarchyKey];

  const hasHierarchInOrdered = (p: TEntity) =>
    hasHierarch(p) &&
    ordered.some((orderedEntity) =>
      hierarchMatches(orderedEntity, p[hierarchyKey])
    );

  const hierarchNotInSet = (p: TEntity) =>
    hasHierarch(p) &&
    !entities.some((orderedEntity) =>
      hierarchMatches(orderedEntity, p[hierarchyKey])
    );

  const recursivelyAddToSortedList = (iteration = 0): void => {
    if (iteration > options.maxIterations) {
      // probably a circular dependency.
      if (options.throwOnTooManyIterations) {
        throw new KazooError("Cannot sort list.  Iterations exceeded.", {
          iteration,
          max: options.maxIterations,
        });
      }
      entities.filter(notInResults).map((p) => ordered.push(p));
      return;
    }
    entities
      .filter(notInResults)
      .filter(hasHierarchInOrdered)
      .map((p) => ordered.push(p));
    if (entities.some(notInResults)) {
      return recursivelyAddToSortedList(iteration + 1);
    }
  };

  // let's do it

  const ordered = entities.filter((entity) => !hasHierarch(entity));

  // add hierarch outside of the set
  entities
    .filter(notInResults)
    .filter(hasHierarch)
    .filter(hierarchNotInSet)
    .map((p) => ordered.push(p));
  // recurse through entities with hierarchs in the set
  recursivelyAddToSortedList();
  return ordered;
}
